import React, { useState } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Search, AlertTriangle, CheckCircle2, TrendingUp, XCircle, FileText, Info, ArrowLeft } from "lucide-react";
import { allPartiesIncludingNonParliamentary } from "@/data/parties";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useRingStore, submitFactCheck, submitRingArgument } from "@/data/ringStore";
import { useAuth } from "@/contexts/AuthContext";
import { Checkbox } from "@/components/ui/checkbox";
import { translations } from "@/i18n/translations";
import { useGamificationStore } from "@/data/gamificationStore";
import SEO from "@/components/SEO";

// The arguments will be loaded from the store
type ReactionType = 'convincing' | 'sourced' | 'demagogic' | 'offtopic';

const RingPage = () => {
  const { lang, isRtl } = useI18n();
  const [expandedArgs, setExpandedArgs] = useState<Record<string, boolean>>({});
  const [showAllArgsCount, setShowAllArgsCount] = useState(false);
  const [showFullLeaderboard, setShowFullLeaderboard] = useState(false);
  const [expandedPreviousRoundId, setExpandedPreviousRoundId] = useState<number | null>(null);
  const { isFactCheckEnabled, rounds, isRingResultsEnabled, arguments: storeArgs, userReactions, setUserReactions } = useRingStore();
  const activeRound = rounds.find(r => r.status === 'open' || r.status === 'closed');

  const THEME_TRANSLATIONS: Record<string, { ar: string, amz: string }> = {
    "Éducation": { ar: "التعليم", amz: "ⴰⵙⴳⵎⵉ" },
    "Santé": { ar: "الصحة", amz: "ⵜⴰⴷⵓⵙⵉ" },
    "Économie": { ar: "الاقتصاد", amz: "ⵜⴰⴷⴰⵎⵙⴰ" },
    "Environnement": { ar: "البيئة", amz: "ⵜⴰⵡⵏⵏⴰⴹⵜ" },
    "Social": { ar: "اجتماعي", amz: "ⴰⵏⴰⵎⵓⵏ" },
    "Justice": { ar: "العدالة", amz: "ⵜⴰⵣⵔⴼⵜ" },
    "Emploi": { ar: "التشغيل", amz: "ⴰⵙⵡⵓⵔⵉ" },
    "Libertés": { ar: "الحريات", amz: "ⵜⵉⵍⴻⵍⵍⵉⵢⵉⵏ" },
    "Institutions": { ar: "المؤسسات", amz: "ⵜⵉⵎⵔⵙⴰⵍ" },
    "Culture": { ar: "الثقافة", amz: "ⵜⴰⴷⵍⵙⴰ" },
    "Politique": { ar: "سياسة", amz: "ⵜⴰⵙⵔⵜⵉⵜ" },
    "Écologie & Transport": { ar: "البيئة والنقل", amz: "ⵜⴰⵡⵏⵏⴰⴹⵜ ⴷ ⵡⴰⵙⵙⴰⵢ" },
    "Agriculture": { ar: "الفلاحة", amz: "ⵜⴰⴼⵍⵍⴰⵃⵜ" },
    "Infrastructure": { ar: "البنية التحتية", amz: "ⵜⴰⵙⵉⵍⴰ" }
  };

  const getTranslatedCategory = (cat: string, targetLang: 'ar' | 'amz') => {
    // If the exact category matches
    if (THEME_TRANSLATIONS[cat]) return THEME_TRANSLATIONS[cat][targetLang];

    // Check if it's a compound category like "Économie / Emploi" or just try to find a partial match
    for (const [key, trans] of Object.entries(THEME_TRANSLATIONS)) {
      if (cat.includes(key)) {
        return trans[targetLang];
      }
    }
    return cat;
  };

  const currentTopic = activeRound ? {
    round: activeRound.roundNumber,
    categoryFr: activeRound.category,
    categoryAr: getTranslatedCategory(activeRound.category, 'ar'),
    categoryAmz: getTranslatedCategory(activeRound.category, 'amz'),
    questionFr: activeRound.questionFr,
    questionAr: activeRound.questionAr || activeRound.questionFr,
    questionAmz: activeRound.questionAmz || activeRound.questionFr,
    daysLeft: activeRound.status === 'closed' ? 0 : (activeRound.daysLeft || 3),
    participants: 4
  } : {
    round: 12,
    categoryFr: "Écologie & Transport",
    categoryAr: "البيئة والنقل",
    categoryAmz: "ⵜⴰⵡⵏⵏⴰⴹⵜ ⴷ ⵡⴰⵙⵙⴰⵢ",
    questionFr: "Faut-il interdire les véhicules thermiques dans les centres-villes d'ici 2030 ?",
    questionAr: "هل يجب حظر السيارات ذات محركات الاحتراق في مراكز المدن بحلول عام 2030؟",
    questionAmz: "ⵉⵙ ⵉⵇⵇⴰⵏ ⴰⴷ ⵏⴳⴷⵍ ⵜⵉⵀⵉⵔⵉⵜⵉⵏ ⵏ ⵜⵉⵔⵖⵉ ⴳ ⵡⴰⵎⵎⴰⵙ ⵏ ⵜⵉⵖⵔⵎⵉⵏ ⴰⵔ 2030?",
    daysLeft: 3,
    participants: 4
  };
  const { toast } = useToast();

  // Fact-Check States
  const [factCheckArgId, setFactCheckArgId] = useState<string | null>(null);
  const [factCheckParty, setFactCheckParty] = useState<string>("");
  const [factCheckSource, setFactCheckSource] = useState("");
  const [factCheckExplanation, setFactCheckExplanation] = useState("");
  const [authMessageId, setAuthMessageId] = useState<string | null>(null);
  const { addXp, unlockBadge } = useGamificationStore();

  // Contribution States
  const { user, isAuthenticated } = useAuth();
  const [isContributionModalOpen, setIsContributionModalOpen] = useState(false);
  const [contributionContent, setContributionContent] = useState("");
  const [hasAgreedEthics, setHasAgreedEthics] = useState(false);

  const handleContributionSubmit = () => {
    if (activeRound && user?.partyAcronym) {
      submitRingArgument(activeRound.id, user.partyAcronym, contributionContent);
    }
    toast({ title: "Contribution publiée !", description: "Votre argumentaire est désormais visible sur le Ring." });
    setIsContributionModalOpen(false);
    setContributionContent("");
    setHasAgreedEthics(false);
  };

  const handleOpenFactCheck = (argId: string, partyAcronym: string) => {
    setFactCheckArgId(argId);
    setFactCheckParty(partyAcronym);
    setFactCheckSource("");
    setFactCheckExplanation("");
  };

  const handleSubmitFactCheck = () => {
    if (!factCheckSource.trim()) {
      toast({ title: "Erreur", description: "Le lien source est obligatoire.", variant: "destructive" });
      return;
    }
    submitFactCheck({
      argumentId: factCheckArgId!,
      partyAcronym: factCheckParty,
      sourceUrl: factCheckSource,
      explanation: factCheckExplanation
    });
    toast({ title: "Signalement envoyé", description: "Votre proposition a été transmise aux modérateurs." });
    setFactCheckArgId(null);
    addXp(20, "Vérification de faits");
    unlockBadge('fact_checker');
  };

  const t = (fr: string, ar: string, amz: string) => {
    if (lang === 'ar') return ar;
    if (lang === 'zgh') return amz;
    return fr;
  };

  const toggleExpand = (argId: string) => {
    setExpandedArgs(prev => ({ ...prev, [argId]: !prev[argId] }));
  };

  const handleReact = (argId: string, reaction: ReactionType) => {
    if (!isAuthenticated) {
      if (window.innerWidth < 768) {
        toast({ title: t("Connexion requise", "تسجيل الدخول مطلوب", "ⵉⵇⵇⴰⵏ ⴷ ⴰⵙⵙⵏⵜⵎ"), description: t("Veuillez vous connecter pour voter.", "يرجى تسجيل الدخول للتصويت.", "ⵎⵇⵇⴰⵔ ⵜⴽⵛⵎⵎ ⴰⴼⴰⴷ ⴰⴷ ⵜⵙⵜⴰⵢⵎ.") });
      } else {
        setAuthMessageId(argId);
      }
      return;
    }
    
    // Optimistic UI Update handled by store
    const newReaction = userReactions[argId] === reaction ? 'remove' : reaction;
    import("@/data/ringStore").then(m => m.submitReaction(argId, newReaction));
  };

  const getReactionStyle = (argId: string, type: ReactionType, activeBg: string, hoverBg: string, hoverText: string) => {
    const isActive = userReactions[argId] === type;
    return isActive
      ? `${activeBg} text-white border-transparent`
      : `bg-background border-border ${hoverBg} ${hoverText}`;
  };

  const activeRoundArgs = storeArgs.filter(a => a.roundId === activeRound?.id && a.status === 'approved');
  const sortedArgs = [...activeRoundArgs].sort((a, b) => {
    const dateA = new Date(a.createdAt.replace(' ', 'T')).getTime();
    const dateB = new Date(b.createdAt.replace(' ', 'T')).getTime();
    return dateB - dateA;
  });
  const displayedArgs = showAllArgsCount ? sortedArgs : sortedArgs.slice(0, 6);

  const leaderboardScores = activeRoundArgs.map(arg => {
    const r = arg.reactions || { convincing: 0, sourced: 0, demagogic: 0, offtopic: 0 };
    const score = (r.convincing * 2) + r.sourced - r.demagogic - (r.offtopic * 2);
    return {
      partyAcronym: arg.partyAcronym,
      partyName: allPartiesIncludingNonParliamentary.find(p => p.acronym === arg.partyAcronym)?.name || arg.partyAcronym,
      score,
      convincing: r.convincing,
      sourced: r.sourced,
      demagogic: r.demagogic,
      offtopic: r.offtopic
    };
  }).sort((a, b) => b.score - a.score);

  const displayedLeaderboard = showFullLeaderboard ? leaderboardScores : leaderboardScores.slice(0, 3);

  const MOCK_SEASON_SCORES = leaderboardScores.map(s => ({ ...s, score: s.score * 5, convincing: s.convincing * 5, sourced: s.sourced * 5, demagogic: s.demagogic * 5, offtopic: s.offtopic * 5 })).sort((a, b) => b.score - a.score);
  const displayedSeasonLeaderboard = showFullLeaderboard ? MOCK_SEASON_SCORES : MOCK_SEASON_SCORES.slice(0, 3);

  const PREVIOUS_ROUNDS = [
    { id: 11, monthFr: "Mai 2026", monthAr: "ماي 2026", monthAmz: "ⵎⴰⵢⵢⵓ 2026", scores: leaderboardScores.map(s => ({ ...s, score: Math.max(0, s.score - 10), convincing: Math.max(0, s.convincing - 5) })).sort((a, b) => b.score - a.score) },
    { id: 10, monthFr: "Avril 2026", monthAr: "أبريل 2026", monthAmz: "ⵉⴱⵔⵉⵔ 2026", scores: leaderboardScores.map(s => ({ ...s, score: Math.max(0, s.score - 20), convincing: Math.max(0, s.convincing - 10) })).sort((a, b) => b.score - a.score) },
  ];

  return (
    <>
      <SEO 
        title="Kayn'in | L'Arène des Partis"
        description="Les partis s'affrontent sur les grands enjeux. Analysez leurs arguments et rendez votre verdict."
        url="https://kaynin.ma/ring"
      />
      <Navbar />
      <div className="min-h-screen pt-24 pb-12 bg-[#f8fafc] dark:bg-background">
        <div className="container max-w-7xl mx-auto px-4">

          <Button variant="ghost" className="mb-8 font-body -ml-4 hover:bg-transparent hover:text-primary transition-colors text-muted-foreground" asChild>
            <Link to="/">
              <ArrowLeft className="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0 rtl:rotate-180" />
              {t("Retour à l'accueil", "العودة إلى الرئيسية", "ⴰⵖⵓⵍ ⵖⵔ ⵜⴰⵙⵏⴰ ⵏ ⵓⵙⵏⵓⴱⴳ")}
            </Link>
          </Button>

          {/* Header Section */}
          <div className="flex flex-col md:flex-row justify-between items-start bg-white dark:bg-card border-2 border-border/50 rounded-3xl p-6 md:p-8 mb-8 shadow-sm">
            <div className="flex-1 mb-6 md:mb-0">
              <h1 className="text-4xl md:text-5xl font-black tracking-tighter uppercase text-primary mb-2 flex justify-center items-center gap-3 text-center w-full">
                {t("L'Arène des Partis", "حلبة الأحزاب", "ⴰⵙⴰⵔⴰⴳ ⵏ ⵉⴽⴰⴱⴰⵕⵏ")}
              </h1>
              <p className="text-lg text-muted-foreground font-medium mb-6 text-center mx-auto md:whitespace-nowrap">
                {t("Les partis s'affrontent sur les grands enjeux. Analysez leurs arguments et rendez votre verdict.", "تتنافس الأحزاب حول القضايا الكبرى. حلل حججهم وأصدر حكمك.", "ⵉⴽⴰⴱⴰⵕⵏ ⴰⵔ ⵜⵜⵎⵏⴰⵖⵏ ⵅⴼ ⵜⵎⵙⴰⵔⵉⵏ ⵜⵉⵎⵇⵇⵔⴰⵏⵉⵏ. ⴼⵙⵢ ⵉⵙⵓⵔⴰⴼⵏ ⵏⵏⵙⵏ ⵜⴼⴽⴷ ⴰⵙⵜⴰⵢ ⵏⵏⴽ.")}
              </p>

              <div className="bg-muted/30 rounded-2xl p-5 border border-border/50 relative overflow-hidden">
                <div className={`absolute top-0 ${isRtl ? 'right-0' : 'left-0'} w-1.5 h-full bg-red-600`}></div>
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <Badge variant="destructive" className="animate-pulse bg-red-600">
                    <span className="w-1.5 h-1.5 rounded-full bg-white mr-2"></span>
                    {t("Round", "الجولة", "ⵜⴰⵡⴰⵍⴰ")} {currentTopic.round}
                  </Badge>
                  <Badge variant="outline" className="text-muted-foreground border-border bg-white dark:bg-background">
                    {t(currentTopic.categoryFr, currentTopic.categoryAr, currentTopic.categoryAmz)}
                  </Badge>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground mb-4">
                  {t(currentTopic.questionFr, currentTopic.questionAr, currentTopic.questionAmz)}
                </h2>
                {isRingResultsEnabled && (
                  <div className="flex flex-wrap gap-2">
                    <Button variant="default" size="sm" onClick={() => document.getElementById('round-results')?.scrollIntoView({ behavior: 'smooth' })} className="font-bold">
                      {t("Résultats du Round", "نتائج الجولة", "ⵉⴳⵎⴰⴹⵏ ⵏ ⵜⵡⴰⵍⴰ")}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => document.getElementById('season-results')?.scrollIntoView({ behavior: 'smooth' })} className="font-bold bg-white text-primary hover:bg-muted border-primary/20">
                      {t("Le Classement (Saison 1)", "الترتيب (الموسم 1)", "ⴰⵙⵎⵉⵍ (ⴰⵏⵎⵓⴳⴳⴰⵔ 1)")}
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-row md:flex-col justify-center gap-4 text-center md:text-right shrink-0 md:pl-8 md:ml-6 md:border-l border-border/50 self-stretch">
              <div>
                <div className="text-3xl font-black text-primary">{currentTopic.daysLeft}</div>
                <div className="text-xs uppercase font-bold text-muted-foreground tracking-wider">
                  {t("Jours restants", "أيام متبقية", "ⵓⵙⵙⴰⵏ ⵉⵖⴰⵎⴰⵏ")}
                </div>
              </div>
              <div>
                <div className="text-xl font-bold text-foreground">{currentTopic.participants}</div>
                <div className="text-xs uppercase font-bold text-muted-foreground tracking-wider">
                  {t("Partis participants", "أحزاب مشاركة", "ⵉⴽⴰⴱⴰⵕⵏ ⵉⴷⵔⴰⵏ")}
                </div>
              </div>

              {user?.role === 'PARTY' && (
                <div className="mt-2 pt-4 border-t border-border/50 md:border-t-0 md:pt-0">
                  <Dialog open={isContributionModalOpen} onOpenChange={setIsContributionModalOpen}>
                    <DialogTrigger asChild>
                      <Button className="w-full font-bold bg-red-600 hover:bg-red-700 text-white shadow-sm mt-4">
                        {t("Accéder au Ring", "دخول الحلبة", "ⴽⵛⵎ ⵙ ⵓⵙⴰⵔⴰⴳ")}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[700px]">
                      <DialogHeader>
                        <DialogTitle className="text-2xl">{t("Soumettre votre contribution", "تقديم مساهمتك", "ⴰⵣⵏ ⵜⵉⵡⵉⵙⵉ ⵏⵏⴽ")}</DialogTitle>
                        <DialogDescription className="text-base mt-2">
                          {t("Publiez l'argumentaire de votre parti sur le débat en cours.", "انشر حجج حزبك حول النقاش الحالي.", "ⴼⵙⵔ ⵉⵙⵓⵔⴰⴼⵏ ⵏ ⵓⴽⴰⴱⴰⵕ ⵏⵏⴽ ⵅⴼ ⵓⵎⴹⴰⴽⴰⵕ ⴰⴷ.")}
                        </DialogDescription>
                      </DialogHeader>

                      <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-3 my-2 flex items-start gap-3 text-blue-800 text-sm">
                        <Info className="w-5 h-5 shrink-0 mt-0.5 text-blue-600" />
                        <div>
                          <p className="font-semibold">{t("Préconisations stratégiques", "توصيات استراتيجية", "ⴰⵙⵎⵎⴰⵜⵔ ⴰⵙⵜⵔⴰⵜⵉⵊⵉ")}</p>
                          <p className="mt-1 opacity-90">
                            {t("Soyez précis, concis et ciblé. Les citoyens évaluent directement la qualité de votre argumentaire.", "كن دقيقًا، موجزًا وموجهًا. يقوم المواطنون بتقييم جودة حججك مباشرة.", "ⴳⴰⵜ ⵉⵏⵎⴷⴰⵏⵏ, ⵉⴳⵣⵣⵓⵍⵏ ⴷ ⵉⵎⵙⵡⴰⵜⵏ. ⴰⵔ ⵙⵙⵉⵜⴳⵏ ⵉⵏⴰⵎⵓⵔⵏ ⵜⵉⵖⴰⵔⵉⵡⵉⵏ ⵏ ⵉⵙⵓⵔⴰⴼⵏ ⵏⵏⵓⵏ ⵙ ⵓⵙⵔⵉⴷ.")}
                          </p>
                        </div>
                      </div>

                      <div className="grid gap-4 py-2">
                        <div className="relative">
                          <Textarea
                            placeholder={t("Votre vision et stratégie...", "رؤيتكم واستراتيجيتكم...", "ⵜⴰⵏⵏⴰⵢⵜ ⴷ ⵜⵙⵜⵔⴰⵜⵉⵊⵉⵜ ⵏⵏⵓⵏ...")}
                            value={contributionContent}
                            onChange={(e) => setContributionContent(e.target.value.substring(0, 1000))}
                            className="min-h-[250px] resize-y text-base p-4 focus-visible:ring-red-500 pb-8"
                          />
                          <div className={`absolute bottom-3 right-3 text-xs font-semibold ${contributionContent.length >= 1000 ? 'text-red-600' : 'text-muted-foreground'}`}>
                            {contributionContent.length} / 1000 {t("caractères", "حرف", "ⵉⵙⴽⴽⵉⵍⵏ")}
                          </div>
                        </div>

                        <div className="text-xs text-muted-foreground flex items-center gap-1.5 -mt-2">
                          <Info className="w-3.5 h-3.5" />
                          {t(
                            "Vous êtes libre de rédiger dans la ou les langues de votre choix (aucune traduction automatique).",
                            "أنت حر في الكتابة باللغة أو اللغات التي تختارها (بدون ترجمة آلية).",
                            "ⵜⵉⵍⵉⴷ ⴰⵙⵔⴼ ⴰⴷ ⵜⴰⵔⴰⴷ ⵙ ⵜⵓⵜⵍⴰⵢⵉⵏ ⵏⵏⴰ ⵜⵔⵉⴷ (ⴱⵍⴰ ⴰⵙⵓⵖⵍ ⴰⵡⵜⵓⵎⴰⵜⵉⴽⵉ)."
                          )}
                        </div>

                        <div className="flex items-start space-x-3 mt-4 bg-muted/30 p-4 rounded-lg border border-border/50">
                          <Checkbox
                            id="ethics"
                            checked={hasAgreedEthics}
                            onCheckedChange={(c) => setHasAgreedEthics(!!c)}
                            className="mt-0.5"
                          />
                          <Label htmlFor="ethics" className="text-sm font-medium leading-snug cursor-pointer">
                            {t("En soumettant cette contribution, le parti assume son entière responsabilité et s'engage à respecter la déclaration d'éthique de la plateforme.", "بتقديم هذه المساهمة، يتحمل الحزب مسؤوليته الكاملة ويلتزم باحترام الميثاق الأخلاقي للمنصة.", "ⵙ ⵓⵣⴰⵏ ⵏ ⵜⵉⵡⵉⵙⵉ ⴰⴷ, ⴰⴽⴰⴱⴰⵕ ⴰⵔ ⵉⵜⵜⴰⵙⵉ ⵜⴰⵎⵙⵜⴰⴳⴳⴰⵔⵜ ⵏⵏⵙ ⴰⴽⴽⵯ ⴷ ⴰⵔ ⵉⵜⵜⴰⵊⵊⴰ ⴰⴷ ⵉⵇⵇⴰⴷⵔ ⴰⵎⵙⴰⵙⴰ ⵏ ⵜⵖⵍⴰⴼ ⵏ ⵓⵙⴰⵢⵔⴰⵔ.")}
                          </Label>
                        </div>
                      </div>
                      <DialogFooter className="pt-2">
                        <Button
                          onClick={handleContributionSubmit}
                          disabled={!hasAgreedEthics || contributionContent.length < 10}
                          className="w-full bg-red-600 hover:bg-red-700 text-white"
                        >
                          {t("Publier sur le Ring", "النشر في الحلبة", "ⴼⵙⵔ ⴳ ⵓⵙⴰⵔⴰⴳ")}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              )}
            </div>
          </div>

          {/* Arguments Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {displayedArgs.map((arg) => {
              const partyData = allPartiesIncludingNonParliamentary.find(p => p.acronym === arg.partyAcronym);
              const logoToUse = partyData?.logoUrl;

              return (
                <Card key={arg.id} className="relative overflow-hidden border-2 transition-all duration-300 hover:shadow-xl group" style={{ borderColor: `${partyData?.color || "#94a3b8"}20` }}>
                  <div className="absolute top-0 left-0 w-full h-1.5" style={{ backgroundColor: partyData?.color || "#94a3b8" }}></div>

                  <CardHeader className="pb-4">
                    <div className="flex justify-between items-start">
                      <Link to={`/parti/${arg.partyAcronym}`} className="group-hover:text-primary transition-colors cursor-pointer block">
                        <h3 className="font-black text-xl mb-1">{arg.partyAcronym}</h3>
                        <p className="text-xs text-muted-foreground font-medium group-hover:text-foreground transition-colors">{translations[lang as 'fr' | 'ar' | 'zgh']?.parties?.partiesNames?.[arg.partyAcronym] || partyData?.name}</p>
                      </Link>
                      <Link to={`/parti/${arg.partyAcronym}`} className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold cursor-pointer hover:opacity-90 transition-opacity overflow-hidden shrink-0 shadow-sm border border-border/50" style={{ backgroundColor: partyData?.color || "#94a3b8" }}>
                        {logoToUse ? (
                          <img src={logoToUse} alt={arg.partyAcronym} className="w-full h-full object-cover bg-white" />
                        ) : (
                          arg.partyAcronym.substring(0, 2)
                        )}
                      </Link>
                    </div>
                  </CardHeader>

                  <CardContent className="pb-6">
                    <div className="text-[15px] leading-relaxed text-foreground/90 font-medium">
                      {arg.content.length > 200 && !expandedArgs[arg.id] ? (
                        <>
                          "{arg.content.substring(0, 200)}..."
                          <button onClick={() => toggleExpand(arg.id)} className="text-primary font-bold ml-2 text-sm hover:underline">
                            {t("Voir plus", "عرض المزيد", "ⵥⵕ ⵓⴳⴳⴰⵔ")}
                          </button>
                        </>
                      ) : (
                        <>
                          "{arg.content}"
                          {arg.content.length > 200 && (
                            <button onClick={() => toggleExpand(arg.id)} className="text-primary font-bold ml-2 text-sm hover:underline">
                              {t("Voir moins", "عرض أقل", "ⵥⵕ ⴷⵔⵓⵙ")}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </CardContent>

                  <CardFooter className="flex-col gap-3 pt-4 border-t bg-muted/20">
                    <div className="w-full text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1 text-center">
                      {t("Comment jugez-vous cet argument ?", "كيف تقيم هذا الحجة؟", "ⵎⴰⵏⵉⴽ ⴰⵔ ⵜⵜⵉⵎⵉⵔⵜ ⵉⵙⵓⵔⴰⴼⵏ ⴰⴷ؟")}
                    </div>

                    <div className="grid grid-cols-2 gap-2 w-full">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleReact(arg.id, 'convincing')}
                        className={`flex items-center justify-between transition-all ${getReactionStyle(arg.id, 'convincing', 'bg-green-600', 'hover:bg-green-50', 'hover:text-green-700')}`}
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        <span className="text-[10px] uppercase font-bold tracking-wider">
                          {t("Convaincant", "مقنع", "ⵉⵙⵙⵏⵄⵜ")}
                        </span>
                        <span className="text-[10px] opacity-70">{arg.reactions?.convincing || 0}</span>
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleReact(arg.id, 'sourced')}
                        className={`flex items-center justify-between transition-all ${getReactionStyle(arg.id, 'sourced', 'bg-blue-600', 'hover:bg-blue-50', 'hover:text-blue-700')}`}
                      >
                        <FileText className="w-4 h-4" />
                        <span className="text-[10px] uppercase font-bold tracking-wider">
                          {t("Sourcé", "مدعوم بالمصادر", "ⵉⵙⴰⵖⵓⵍⵏ")}
                        </span>
                        <span className="text-[10px] opacity-70">{arg.reactions?.sourced || 0}</span>
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleReact(arg.id, 'demagogic')}
                        className={`flex items-center justify-between transition-all ${getReactionStyle(arg.id, 'demagogic', 'bg-orange-500', 'hover:bg-orange-50', 'hover:text-orange-700')}`}
                      >
                        <AlertTriangle className="w-4 h-4" />
                        <span className="text-[10px] uppercase font-bold tracking-wider">
                          {t("Démagogique", "غوغائي", "ⴰⵎⵙⴳⴷ")}
                        </span>
                        <span className="text-[10px] opacity-70">{arg.reactions?.demagogic || 0}</span>
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleReact(arg.id, 'offtopic')}
                        className={`flex items-center justify-between transition-all ${getReactionStyle(arg.id, 'offtopic', 'bg-gray-600', 'hover:bg-gray-50', 'hover:text-gray-700')}`}
                      >
                        <XCircle className="w-4 h-4" />
                        <span className="text-[10px] uppercase font-bold tracking-wider">
                          {isRtl ? "خارج الموضوع" : "Hors-sujet"}
                        </span>
                        <span className="text-[10px] opacity-70">{arg.reactions?.offtopic || 0}</span>
                      </Button>
                    </div>

                    {isFactCheckEnabled && (
                      <Dialog open={factCheckArgId === arg.id} onOpenChange={(open) => {
                        if (!open) setFactCheckArgId(null);
                      }}>
                        <DialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full mt-2 text-xs text-muted-foreground hover:text-red-600 hover:bg-red-50"
                            onClick={(e) => {
                              if (!isAuthenticated) {
                                e.preventDefault();
                                if (window.innerWidth < 768) {
                                  toast({
                                    title: t("Connexion requise", "تسجيل الدخول مطلوب", "ⵉⵇⵇⴰⵏ ⴷ ⴰⵙⵙⵏⵜⵎ"),
                                    description: t("Veuillez vous connecter ou créer un compte pour pouvoir fact-checker.", "يرجى تسجيل الدخول أو إنشاء حساب.", "ⵎⵇⵇⴰⵔ ⵜⴽⵛⵎⵎ ⵏⵖ ⵜⵙⴽⵔⵎ ⴰⵎⵉⴹⴰⵏ."),
                                    variant: "default"
                                  });
                                } else {
                                  setAuthMessageId(arg.id);
                                }
                                return;
                              }
                              handleOpenFactCheck(arg.id, arg.partyAcronym);
                            }}
                          >
                            <Search className="w-3 h-3 mr-2" />
                            {t("Fact-checker une affirmation", "التحقق من الحقائق (Fact-check)", "ⵙⵙⵏⵜⵍ ⵜⵉⴷⵜ")}
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-[425px]">
                          <DialogHeader>
                            <DialogTitle>{t("Soumettre un Fact-Check", "إرسال تدقيق للحقائق", "ⴰⵣⵏ ⴰⵙⵙⵏⵜⵍ ⵏ ⵜⵉⴷⵜ")}</DialogTitle>
                            <DialogDescription>
                              Aidez-nous à vérifier cette affirmation de <b>{arg.partyAcronym}</b> en fournissant une source fiable.
                            </DialogDescription>
                          </DialogHeader>
                          <div className="grid gap-4 py-4">
                            <div className="grid gap-2">
                              <Label htmlFor="source">Lien de la source (officiel, média fiable) *</Label>
                              <Input id="source" placeholder="https://..." value={factCheckSource} onChange={(e) => setFactCheckSource(e.target.value)} />
                            </div>
                            <div className="grid gap-2">
                              <Label htmlFor="explanation">Votre explication (optionnelle, max 60 caractères)</Label>
                              <Textarea id="explanation" placeholder="Détaillez pourquoi l'affirmation est trompeuse..." value={factCheckExplanation} onChange={(e) => setFactCheckExplanation(e.target.value)} maxLength={60} />
                            </div>
                          </div>
                          <DialogFooter>
                            <Button type="button" onClick={handleSubmitFactCheck} className="w-full">
                              Envoyer aux modérateurs
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    )}

                    {/* Desktop Inline Auth Message */}
                    {authMessageId === arg.id && !isAuthenticated && (
                      <div
                        className="hidden md:flex items-start bg-red-50 text-red-800 p-2.5 rounded-md text-xs mt-1 border border-red-200 cursor-pointer animate-in fade-in zoom-in-95 duration-200 w-full"
                        onClick={() => setAuthMessageId(null)}
                      >
                        <Info className="w-4 h-4 mr-2 shrink-0 mt-0.5 text-red-600" />
                        <div>
                          <span className="font-bold">{t("Connexion requise", "تسجيل الدخول مطلوب", "ⵉⵇⵇⴰⵏ ⴷ ⴰⵙⵙⵏⵜⵎ")}</span><br />
                          <span className="opacity-90">{t("Veuillez vous connecter pour fact-checker.", "يرجى تسجيل الدخول للتحقق.", "ⵎⵇⵇⴰⵔ ⵜⴽⵛⵎⵎ ⴰⴼⴰⴷ ⴰⴷ ⵜⵙⵙⵏⵜⵍⵎ.")}</span>
                        </div>
                      </div>
                    )}
                  </CardFooter>
                </Card>
              )
            })}
          </div>

          {sortedArgs.length > 6 && !showAllArgsCount && (
            <div className="mt-8 text-center">
              <Button variant="outline" className="px-8 font-bold" onClick={() => setShowAllArgsCount(true)}>
                {t("Voir plus", "عرض المزيد", "ⵥⵕ ⵓⴳⴳⴰⵔ")}
              </Button>
            </div>
          )}

          {/* Leaderboard Sections */}
          {isRingResultsEnabled && (
            <div id="round-results" className="mt-16">
              <div className="flex items-center gap-3 mb-6">
                <TrendingUp className="w-8 h-8 text-primary" />
                <h2 className="text-2xl font-black tracking-tight">{t("Résultats du Round Actuel", "نتائج الجولة الحالية", "ⵉⴳⵎⴰⴹⵏ ⵏ ⵜⵡⴰⵍⴰ ⵏ ⵖⵉⵍⴰ")}</h2>
              </div>
              <Card className="border-2 border-border/50 shadow-sm overflow-hidden mb-12">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-start" dir={isRtl ? "rtl" : "ltr"}>
                    <thead className="text-xs text-muted-foreground uppercase bg-muted/50 border-b border-border/50">
                      <tr>
                        <th className="px-6 py-4 font-bold">{t("Position", "المركز", "ⴰⵏⵙⴰ")}</th>
                        <th className="px-6 py-4 font-bold">{t("Parti Politique", "الحزب السياسي", "ⴰⴽⴰⴱⴰⵕ ⴰⵙⵔⵜⴰⵏ")}</th>
                        <th className="px-6 py-4 text-center font-bold">{t("Score Global", "المجموع العام", "ⴰⵎⵓⵖ ⴰⵎⴰⵜⴰⵢ")}</th>
                        <th className="px-6 py-4 text-center font-bold text-green-600">💡 {t("Convaincant", "مقنع", "ⵉⵙⵙⵏⵄⵜ")}</th>
                        <th className="px-6 py-4 text-center font-bold text-blue-600">📊 {t("Sourcé", "مدعوم بالمصادر", "ⵉⵙⴰⵖⵓⵍⵏ")}</th>
                        <th className="px-6 py-4 text-center font-bold text-orange-500">🗣️ {t("Démagogique", "غوغائي", "ⴰⵎⵙⴳⴷ")}</th>
                        <th className="px-6 py-4 text-center font-bold text-gray-500">🚫 {t("Hors-sujet", "خارج الموضوع", "ⴱⵕⵕⴰ ⵏ ⵓⵙⵏⵜⵍ")}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/50">
                      {displayedLeaderboard.map((item, idx) => (
                        <tr key={item.partyAcronym} className={`${idx === 0 ? "bg-primary/5 hover:bg-primary/10" : "hover:bg-muted/30"} transition-colors`}>
                          <td className={`px-6 py-4 font-black text-xl flex items-center gap-2 ${idx === 0 ? "text-primary" : "text-muted-foreground"}`}>
                            {idx + 1} <span className="text-base">{idx === 0 ? "🏆" : idx === 1 ? "🥈" : idx === 2 ? "🥉" : ""}</span>
                          </td>
                          <td className="px-6 py-4 font-bold">{item.partyAcronym} <span className="hidden sm:inline font-normal opacity-70">({translations[lang as 'fr' | 'ar' | 'zgh']?.parties?.partiesNames?.[item.partyAcronym] || item.partyName})</span></td>
                          <td className="px-6 py-4 text-center font-black text-lg">{item.score} {t("pts", "نقطة", "ⵜⵉⵇⵇⵉⴹⵉⵏ")}</td>
                          <td className="px-6 py-4 text-center font-semibold">{item.convincing}</td>
                          <td className="px-6 py-4 text-center font-semibold">{item.sourced}</td>
                          <td className="px-6 py-4 text-center font-semibold">{item.demagogic}</td>
                          <td className="px-6 py-4 text-center font-semibold">{item.offtopic}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="bg-muted/30 p-4 border-t border-border/50 text-xs text-muted-foreground text-center">
                  {t("Le score global est calculé dynamiquement : (+2) pour Convaincant, (+1) pour Sourcé, (-1) pour Démagogique, (-2) pour Hors-sujet.",
                    "يتم حساب المجموع العام ديناميكيًا: (+2) للمقنع، (+1) للمدعوم بالمصادر، (-1) للغوغائي، (-2) لخارج الموضوع.",
                    "ⴰⵎⵓⵖ ⴰⵎⴰⵜⴰⵢ ⵉⵜⵜⵓⵙⵉⴹⵏ ⵙ ⵓⵙⵓⵎⵓ: (+2) ⵉⵙⵙⵏⵄⵜ, (+1) ⵉⵙⴰⵖⵓⵍⵏ, (-1) ⴰⵎⵙⴳⴷ, (-2) ⴱⵕⵕⴰ ⵏ ⵓⵙⵏⵜⵍ.")}
                </div>
              </Card>

              {/* Previous Rounds Accordion */}
              <div className="mb-16">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <Search className="w-5 h-5 text-muted-foreground" />
                  {t("Voir les résultats des rounds précédents", "عرض نتائج الجولات السابقة", "ⵥⵕ ⵉⴳⵎⴰⴹⵏ ⵏ ⵜⵡⴰⵍⴰ ⵉⵣⵔⵉⵏ")}
                </h3>
                <div className="space-y-3">
                  {PREVIOUS_ROUNDS.map(pr => (
                    <div key={pr.id} className="border-2 border-border/50 rounded-xl overflow-hidden bg-card shadow-sm transition-all">
                      <button
                        onClick={() => setExpandedPreviousRoundId(expandedPreviousRoundId === pr.id ? null : pr.id)}
                        className="w-full flex items-center justify-between p-4 bg-muted/10 hover:bg-muted/30 transition-colors"
                      >
                        <span className="font-black text-lg text-foreground">Round {pr.id} <span className="opacity-60 text-base ml-2">/ {t(pr.monthFr, pr.monthAr, pr.monthAmz)}</span></span>
                        <span className={`transform transition-transform text-muted-foreground font-bold ${expandedPreviousRoundId === pr.id ? "rotate-180" : ""}`}>▼</span>
                      </button>

                      {expandedPreviousRoundId === pr.id && (
                        <div className="border-t-2 border-border/50 overflow-x-auto bg-background/50">
                          <table className="w-full text-sm text-start" dir={isRtl ? "rtl" : "ltr"}>
                            <thead className="text-xs text-muted-foreground uppercase bg-muted/30 border-b border-border/50">
                              <tr>
                                <th className="px-4 py-3 font-bold">{t("Pos", "المركز", "ⴰⵏⵙⴰ")}</th>
                                <th className="px-4 py-3 font-bold">{t("Parti", "الحزب", "ⴰⴽⴰⴱⴰⵕ")}</th>
                                <th className="px-4 py-3 text-center font-bold">{t("Score", "المجموع", "ⴰⵎⵓⵖ")}</th>
                                <th className="px-4 py-3 text-center font-bold text-green-600">💡</th>
                                <th className="px-4 py-3 text-center font-bold text-blue-600">📊</th>
                                <th className="px-4 py-3 text-center font-bold text-orange-500">🗣️</th>
                                <th className="px-4 py-3 text-center font-bold text-gray-500">🚫</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-border/50">
                              {pr.scores.slice(0, 3).map((item, idx) => (
                                <tr key={item.partyAcronym} className="hover:bg-muted/30 transition-colors">
                                  <td className="px-4 py-3 font-bold text-muted-foreground">{idx + 1}</td>
                                  <td className="px-4 py-3 font-bold">{item.partyAcronym}</td>
                                  <td className="px-4 py-3 text-center font-black">{item.score}</td>
                                  <td className="px-4 py-3 text-center font-medium opacity-80">{item.convincing}</td>
                                  <td className="px-4 py-3 text-center font-medium opacity-80">{item.sourced}</td>
                                  <td className="px-4 py-3 text-center font-medium opacity-80">{item.demagogic}</td>
                                  <td className="px-4 py-3 text-center font-medium opacity-80">{item.offtopic}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Season Leaderboard */}
              <div id="season-results" className="pt-8 border-t-2 border-dashed border-border/50">
                <div className="flex items-center gap-3 mb-6">
                  <TrendingUp className="w-8 h-8 text-primary" />
                  <h2 className="text-2xl font-black tracking-tight">{t("Le Classement (Saison 1)", "الترتيب (الموسم 1)", "ⴰⵙⵎⵉⵍ (ⴰⵏⵎⵓⴳⴳⴰⵔ 1)")}</h2>
                </div>
                <Card className="border-2 border-primary/20 shadow-sm overflow-hidden mb-6">
                  <div className="bg-primary/5 p-4 border-b border-primary/10 text-sm text-foreground/80 font-medium">
                    {t("Cumul des points sur l'ensemble des rounds de la saison en cours.", "مجموع النقاط على جميع جولات الموسم الحالي.", "ⵜⴰⵎⵓⵜⵜⵓⵜ ⵏ ⵜⵇⵇⵉⴹⵉⵏ ⵅⴼ ⴰⴽⴽⵯ ⵜⵡⴰⵍⵉⵡⵉⵏ ⵏ ⵓⵏⵎⵓⴳⴳⴰⵔ.")}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-start" dir={isRtl ? "rtl" : "ltr"}>
                      <thead className="text-xs text-muted-foreground uppercase bg-muted/50 border-b border-border/50">
                        <tr>
                          <th className="px-6 py-4 font-bold">{t("Position", "المركز", "ⴰⵏⵙⴰ")}</th>
                          <th className="px-6 py-4 font-bold">{t("Parti Politique", "الحزب السياسي", "ⴰⴽⴰⴱⴰⵕ ⴰⵙⵔⵜⴰⵏ")}</th>
                          <th className="px-6 py-4 text-center font-bold">{t("Score Saison", "مجموع الموسم", "ⴰⵎⵓⵖ ⵏ ⵓⵏⵎⵓⴳⴳⴰⵔ")}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border/50">
                        {displayedSeasonLeaderboard.map((item, idx) => (
                          <tr key={item.partyAcronym} className={`${idx === 0 ? "bg-primary/5 hover:bg-primary/10" : "hover:bg-muted/30"} transition-colors`}>
                            <td className={`px-6 py-4 font-black text-xl flex items-center gap-2 ${idx === 0 ? "text-primary" : "text-muted-foreground"}`}>
                              {idx + 1} <span className="text-base">{idx === 0 ? "🏆" : idx === 1 ? "🥈" : idx === 2 ? "🥉" : ""}</span>
                            </td>
                            <td className="px-6 py-4 font-bold">{item.partyAcronym} <span className="hidden sm:inline font-normal opacity-70">({translations[lang as 'fr' | 'ar' | 'zgh']?.parties?.partiesNames?.[item.partyAcronym] || item.partyName})</span></td>
                            <td className="px-6 py-4 text-center font-black text-xl text-primary">{item.score} {t("pts", "نقطة", "ⵜⵉⵇⵇⵉⴹⵉⵏ")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>

                {!showFullLeaderboard && MOCK_SEASON_SCORES.length > 3 && (
                  <div className="mt-6 text-center">
                    <Button variant="outline" className="gap-2 font-bold hover:bg-primary hover:text-white transition-colors" onClick={() => setShowFullLeaderboard(true)}>
                      {t("Voir tous les résultats de la saison", "عرض جميع نتائج الموسم", "ⵥⵕ ⴰⴽⴽⵯ ⵉⴳⵎⴰⴹⵏ ⵏ ⵓⵏⵎⵓⴳⴳⴰⵔ")}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Back to Top */}
          <div className="mt-8 text-center">
            <Button variant="ghost" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              {t("Retour en haut", "العودة للأعلى", "ⴰⵖⵓⵍ ⵖⵔ ⵓⴼⵍⵍⴰ")} ↑
            </Button>
          </div>

        </div>
      </div>
      <Footer />
    </>
  );
};

export default RingPage;
